import { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Form, Radio } from 'antd';
import { Button, Drawer, DatePicker } from 'ui';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import {
  CartActionType,
  useCartDispatch,
  useCartState,
  WholesaleDeliveryMode,
  WholesaleDeliveryCondition,
} from 'public/context/CartContext';
import { StyledWholesaleDeliveryDrawer, StyledWholesaleDeliveryFooter } from './style';
import DetailedDelivery, { DetailedDeliveryRef } from './DetailedDelivery';
import dayjs from 'dayjs';

interface WholesaleDeliveryDrawerProps {
  open: boolean;
  onClose: () => void;
}

const WholesaleDeliveryDrawer = ({ open, onClose }: WholesaleDeliveryDrawerProps) => {
  const intl = useIntl();
  const dispatch = useCartDispatch();
  const { wholesaleDeliveryMode, wholesaleDeliveries, farmProducts } = useCartState();

  const [form] = Form.useForm();
  const [mode, setMode] = useState<WholesaleDeliveryMode>(wholesaleDeliveryMode || 'single');
  const [detailedDeliveries, setDetailedDeliveries] = useState<WholesaleDeliveryCondition[]>(
    wholesaleDeliveries || []
  );
  const detailedDeliveryRef = useRef<DetailedDeliveryRef>(null);

  useEffect(() => {
    if (open) {
      setMode(wholesaleDeliveryMode || 'single');
      setDetailedDeliveries(wholesaleDeliveries || []);

      if (wholesaleDeliveryMode === 'single' && wholesaleDeliveries?.[0]) {
        form.setFieldsValue({
          delivery_address: wholesaleDeliveries[0].delivery_address,
          delivery_start_date: wholesaleDeliveries[0].delivery_start_date
            ? dayjs(wholesaleDeliveries[0].delivery_start_date)
            : undefined,
        });
      }
    }
  }, [open, wholesaleDeliveryMode, wholesaleDeliveries, form]);

  const handleModeChange = (newMode: WholesaleDeliveryMode) => {
    setMode(newMode);
    if (newMode === 'single') {
      setDetailedDeliveries([]);
    }
  };

  const handleSave = async () => {
    if (mode === 'single') {
      try {
        const values = await form.validateFields();
        const delivery: WholesaleDeliveryCondition = {
          id: wholesaleDeliveries?.[0]?.id || crypto.randomUUID(),
          delivery_address: values.delivery_address,
          delivery_start_date: values.delivery_start_date?.toISOString(),
        };

        dispatch({ type: CartActionType.SET_WHOLESALE_DELIVERY_MODE, mode: 'single' });
        dispatch({ type: CartActionType.SET_WHOLESALE_DELIVERIES, deliveries: [delivery] });
        onClose();
      } catch {
        // Validation failed
      }
    } else {
      if (detailedDeliveries.length === 0) {
        return;
      }

      const isValid = await detailedDeliveryRef.current?.validateAll();
      if (!isValid) {
        return;
      }

      dispatch({ type: CartActionType.SET_WHOLESALE_DELIVERY_MODE, mode: 'detailed' });
      dispatch({ type: CartActionType.SET_WHOLESALE_DELIVERIES, deliveries: detailedDeliveries });
      onClose();
    }
  };

  const getAllCartProducts = () => {
    return farmProducts.flatMap((farm) =>
      farm.products.map((p) => ({
        product_id: p.cartProduct?.id || 0,
        farm_id: farm.farm_id,
        product_name: p.cartProduct?.name || '',
        quantity: p.amount,
        unit: p.cartProduct?.product_amounts?.find((pa) => pa.type === 'WHOLESALE')?.storage_unit,
      }))
    );
  };

  return (
    <Drawer
      title={intl.formatMessage({ id: 'wholesale.delivery.title' })}
      open={open}
      onClose={onClose}
      width={600}
      styled
      footer={
        <StyledWholesaleDeliveryFooter>
          <Button
            onClick={handleSave}
            label={intl.formatMessage({ id: 'wholesale.delivery.add' })}
            className="footer-button"
          />
        </StyledWholesaleDeliveryFooter>
      }
    >
      <StyledWholesaleDeliveryDrawer>
        <div className="delivery-info-text">
          {intl.formatMessage({ id: 'wholesale.delivery.minimum_info' })}
        </div>

        <div className="delivery-mode-selector">
          <Radio.Group value={mode} onChange={(e) => handleModeChange(e.target.value)}>
            <Radio value="single">
              <div className="mode-label">
                {intl.formatMessage({ id: 'wholesale.delivery.to_address' })}
              </div>
              <div className="mode-description">
                {intl.formatMessage({ id: 'wholesale.delivery.to_address_description' })}
              </div>
            </Radio>
            <Radio value="detailed">
              <div className="mode-label">
                {intl.formatMessage({ id: 'wholesale.delivery.detailed' })}
              </div>
              <div className="mode-description">
                {intl.formatMessage({ id: 'wholesale.delivery.detailed_description' })}
              </div>
            </Radio>
          </Radio.Group>
        </div>

        {mode === 'single' && (
          <Form form={form} layout="vertical" className="delivery-form-section">
            <WithoutModalAddressFinder
              form={form}
              name="delivery_address"
              label={intl.formatMessage({ id: 'wholesale.delivery.address' })}
              disabled={false}
              validations="required"
            />
            <DatePicker
              name="delivery_start_date"
              label={intl.formatMessage({ id: 'wholesale.delivery.date' })}
            />
          </Form>
        )}

        {mode === 'detailed' && (
          <DetailedDelivery
            ref={detailedDeliveryRef}
            deliveries={detailedDeliveries}
            onDeliveriesChange={setDetailedDeliveries}
            cartProducts={getAllCartProducts()}
          />
        )}
      </StyledWholesaleDeliveryDrawer>
    </Drawer>
  );
};

export default WholesaleDeliveryDrawer;
